import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import React, {useEffect, useMemo, useState} from 'react';
import withPaginationV2 from '../../../hoc/pagination/withPaginationV2';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import {
    useGetEmployeeNotificationFormsQuery,
    useGetEmployeeLinkedSelfServiceFormsQuery,
    useGetFormsByEmployeeQuery,
    useGetCommunicationFormsByEmployeeQuery,
    useLazyGetEmployeeNotificationFormResponsesQuery,
    useClaimFormRequestMutation,
    useUnclaimFormRequestMutation
} from '../../../api/services/formRequestsService';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {FORM_REQUEST_STATUSES, FORM_REQUEST_STATUSES_NAMES} from '../../../constants/formRequestStatuses';
import FILTER_OPERATIONS from '../../../constants/filterOperations';
import AdmicityStatus from '../../../shared-components/AdmicityStatus';
import {getFormRequestStatusInfo} from '../RequestAndResponses/common/common';
import {formatDate, formatDateTime} from '../../../utility/dateUtil';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    downloadFormResponsePdfReport,
    useLazyGenerateFormResponsesReportByEmployeeQuery
} from '../../../api/services/filesService';
import useFileDownloader from '../../../utility/hooks/useFileDownloader';
import useNotification from '../../../utility/hooks/useNotification';
import AdmicityTabs from '../../../shared-components/AdmicityTabs';
import Paper from '@mui/material/Paper';
import {useTheme} from '@mui/material/styles';
import NotificationFormsTable from '../../../shared-components/NotificationFormsTable';
import ApprovalFormsTable from '../../../shared-components/ApprovalFormsTable';
import {getApprovalStatusInfo} from '../../../constants/FormApprovalStatuses';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Tooltip, IconButton, Box} from '@mui/material';

const expirationHeader = (
    <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5
    }}>
        <span>Expiration Date</span>
        <Tooltip title="Forms will expire on the day at 23:59">
            <IconButton
                size="small"
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <InfoOutlinedIcon fontSize="inherit"/>
            </IconButton>
        </Tooltip>
    </Box>
);

/* eslint-disable react/prop-types */
const allLinkedEmployeesCanSubmit = (row) => {
    const rowData = row?.original ?? row;
    return !!(rowData?.form?.allLinkedEmployeesCanSubmit ?? rowData?.form?.AllLinkedEmployeesCanSubmit);
};

const preSubmittedColumns = [
    {
        accessorKey: 'form.name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        id: 'firstName',
        header: 'First Name',
        accessorFn: (row) => row?.student?.forename || row?.employee?.forename || '',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        id: 'lastName',
        header: 'Last Name',
        accessorFn: (row) => row?.student?.legalSurname || row?.employee?.legalSurname || '',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())} />,
        filterVariant: 'select',
        filterFn: FILTER_OPERATIONS.EQUALS,
        filterSelectOptions: Object.values(FORM_REQUEST_STATUSES)
            .map(statusId => ({
                value: statusId,
                label: FORM_REQUEST_STATUSES_NAMES[statusId]
            })),
    },
    {
        accessorKey: 'expirationDate',
        header: expirationHeader,
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false
    },
];

const claimColumns = [
    {
        accessorKey: 'claimedBy',
        header: 'Claimed By',
        enableColumnFilter: false,
        Cell: ({
                   row,
                   cell
               }) => allLinkedEmployeesCanSubmit(row) ? '' : cell.getValue(),
    },
    {
        accessorKey: 'claimedAt',
        header: 'Claimed At',
        Cell: ({
                   row,
                   cell
               }) =>
            allLinkedEmployeesCanSubmit(row)
                ? ''
                : (cell.getValue() ? formatDate(cell.getValue()) : ''),
        enableColumnFilter: false,
    },
];

const submittedAndApprovalColumns = [
    {
        id: 'editedBy',
        accessorFn: (row) => row?.editedBy?.fullname ?? '',
        header: 'Submitted By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedAt',
        header: 'Submitted At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStage',
        header: 'Approval Stage',
        Cell: ({row}) => row.original?.approvalStage ?? '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStageProgress',
        header: 'Approval Progress',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStatus',
        header: 'Approval Status',
        Cell: ({cell: {getValue}}) => (
            <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
        ),
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedBy',
        header: 'Approved By',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedAt',
        header: 'Approved At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalNote',
        header: 'Approval Note',
        enableColumnFilter: false,
    },
];

const getColumns = (includeClaimColumns = false) => [
    ...preSubmittedColumns,
    ...(includeClaimColumns ? claimColumns : []),
    ...submittedAndApprovalColumns,
];

/* eslint-enable react/prop-types */
const generalFormsTableColumns = [
    {
        accessorKey: 'name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS
    }
];

const linkedSelfServiceFormsTableColumns = [
    {
        accessorKey: 'name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS
    },
    {
        accessorKey: 'linkedPersonName',
        header: 'Linked To',
        filterFn: FILTER_OPERATIONS.CONTAINS
    },
    {
        accessorKey: 'lastSubmittedBy',
        header: 'Last Completed By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'lastSubmittedAt',
        header: 'Last Completed',
        Cell: ({cell}) => cell.getValue() ? formatDateTime(cell.getValue()) : 'Not yet completed',
        enableColumnFilter: false
    }
];

const communicationFormsTableColumns = [
    {
        accessorKey: 'formName',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'sentAt',
        header: 'Date',
        Cell: ({cell}) => {
            const date = cell.getValue();
            return date ? formatDate(date) : '';
        },
        enableColumnFilter: false,
    },
];

const EmployeeForms = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    const {id: employeeId} = useParams();
    const employeeIdNumber = Number(employeeId);
    const navigate = useNavigate();
    const downloadFile = useFileDownloader();
    const {
        showSuccessNotification,
        showErrorNotification
    } = useNotification();
    const [generateFormResponsesReportByEmployee] = useLazyGenerateFormResponsesReportByEmployeeQuery();
    const {
        key,
        formsState,
        setFormsState
    } = useOutletContext();
    const [currentTabIndex, setCurrentTabIndex] = useState(formsState?.currentTabIndex ?? 0);
    const [selectedNotificationFormIndex, setSelectedNotificationFormIndex] = useState(formsState?.selectedNotificationFormIndex);
    const [claimFormRequest, {isLoading: isClaimingFormRequest}] = useClaimFormRequestMutation();
    const [unclaimFormRequest, {isLoading: isUnclaimingFormRequest}] = useUnclaimFormRequestMutation();
    const {
        data: notificationForms = [],
        isLoading: isLoadingNotificationForms,
        isFetching: isFetchingNotificationForms,
    } = useGetEmployeeNotificationFormsQuery(employeeId, {skip: currentTabIndex !== 2});
    const [selectedLinkedFormIndex, setSelectedLinkedFormIndex] = useState(undefined);
    const {
        data: linkedSelfServiceForms = [],
        isLoading: isLoadingLinkedForms,
        isFetching: isFetchingLinkedForms,
    } = useGetEmployeeLinkedSelfServiceFormsQuery(employeeId, {skip: currentTabIndex !== 3});

    const Table = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetFormsByEmployeeQuery,
        props: {
            employeeId,
            linkToStudents: false
        }
    }), [employeeId]);
    const StudentTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetFormsByEmployeeQuery,
        props: {
            employeeId,
            linkToStudents: true
        }
    }), [employeeId]);
    const CommunicationFormsTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetCommunicationFormsByEmployeeQuery,
        props: {employeeId}
    }), [employeeId]);

    useEffect(() => {
        return () => {
            setFormsState(prev => ({
                ...prev,
                currentTabIndex,
                selectedNotificationFormIndex
            }));
        };
    }, [currentTabIndex, selectedNotificationFormIndex]);

    const handleClaimFormRequest = async (formRequestId) => {
        try {
            const payload = {
                formRequestId,
                employeeId: Number(employeeId)
            };

            await claimFormRequest(payload).unwrap();
            showSuccessNotification('Form claimed');
        } catch (error) {
            showErrorNotification(error?.message || 'Failed to claim form request');
        }
    };

    const handleUnclaimFormRequest = async (formRequestId) => {
        try {
            const payload = {
                formRequestId,
                employeeId: Number(employeeId)
            };

            await unclaimFormRequest(payload).unwrap();
            showSuccessNotification('Form unclaimed');
        } catch (error) {
            showErrorNotification(error?.message || 'Failed to unclaim form request');
        }
    };

    const getRowData = (row) => row?.original ?? row;

    const isClaimedByCurrentEmployee = (row) => {
        const rowData = getRowData(row);
        return !!rowData.claimedByEmployeeId && String(rowData.claimedByEmployeeId) === String(employeeId);
    };

    const isClaimedByAnotherEmployee = (row) => {
        const rowData = getRowData(row);
        return !!rowData.claimedByEmployeeId && String(rowData.claimedByEmployeeId) !== String(employeeId);
    };
    const isSubmittedFormRequest = (row) => getRowData(row).status === FORM_REQUEST_STATUSES.submitted;

    const downloadPdfReport = async (formRequestId = null, formId = null, isBulk = true) => {
        if (formRequestId && !isBulk) {
            await downloadFile(
                downloadFormResponsePdfReport,
                {
                    formId,
                    formRequestId
                });
        } else {
            await generateFormResponsesReportByEmployee({employeeId});
            showSuccessNotification('The PDFs is being created. It will be sent to your email address when the process is complete.');
        }
    };

    const tabs =
        [
            {
                label: 'Forms',
                component: <Table
                    id={key}
                    variant="elevation"
                    columns={getColumns(false)}
                    tableProps={{
                        enableSorting: false,
                        enableFullScreenToggle: false
                    }}
                    onRowClick={(params) => navigate(`${params.id}`)}
                    toolbarIconActions={[
                        {
                            icon: <FileDownloadRoundedIcon/>,
                            title: 'Download report',
                            type: 'select',
                            hideIfEmpty: true,
                            menuItems: [
                                {
                                    label: 'Download PDF',
                                    onClick: downloadPdfReport
                                },
                            ]
                        }
                    ]}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No requests and responses yet'
                    }}
                    rowActions={[
                        {
                            label: 'Download PDF',
                            action: params => downloadPdfReport(params.id, params.form.id, false),
                            disabled: row => row.status !== FORM_REQUEST_STATUSES.submitted
                        }
                    ]}
                />
            },
            {
                label: 'Student Forms',
                component: <StudentTable
                    id={`${key}/student-forms`}
                    variant="elevation"
                    columns={getColumns(true)}
                    tableProps={{
                        enableSorting: false,
                        enableFullScreenToggle: false
                    }}
                    onRowClick={(params) => navigate(`${params.id}`, {
                        state: {
                            navigateBackRoute: {
                                pathname: `/profiles/employees/${employeeId}/forms`,
                                state: {activeTabIndex: 1}
                            }
                        }
                    })}
                    toolbarIconActions={[
                        {
                            icon: <FileDownloadRoundedIcon/>,
                            title: 'Download report',
                            type: 'select',
                            hideIfEmpty: true,
                            menuItems: [
                                {
                                    label: 'Download PDF',
                                    onClick: downloadPdfReport
                                },
                            ]
                        }
                    ]}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No student form requests yet'
                    }}
                    rowActions={[
                        {
                            label: 'Claim',
                            action: params => handleClaimFormRequest(params.id),
                            hidden: row => !!allLinkedEmployeesCanSubmit(row) || isSubmittedFormRequest(row),
                            disabled: row =>
                                isClaimedByCurrentEmployee(row) ||
                                isClaimedByAnotherEmployee(row) ||
                                isClaimingFormRequest ||
                                isUnclaimingFormRequest
                        },
                        {
                            label: 'Unclaim',
                            action: params => handleUnclaimFormRequest(params.id),
                            hidden: row => !!allLinkedEmployeesCanSubmit(row) || isSubmittedFormRequest(row),
                            disabled: row =>
                                !isClaimedByCurrentEmployee(row) ||
                                isClaimingFormRequest ||
                                isUnclaimingFormRequest
                        },
                        {
                            label: 'Download PDF',
                            action: params => downloadPdfReport(params.id, params.form.id, false),
                            disabled: row => row.status !== FORM_REQUEST_STATUSES.submitted
                        }
                    ]}
                />
            },
            {
                label: 'Self-service Forms',
                component: <NotificationFormsTable
                    data={notificationForms}
                    columns={generalFormsTableColumns}
                    isLoading={isLoadingNotificationForms || isFetchingNotificationForms}
                    selectedRowIndex={selectedNotificationFormIndex}
                    setSelectedRowIndex={setSelectedNotificationFormIndex}
                    navigate={navigate}
                    personId={employeeIdNumber}
                    fetchResponsesFn={useLazyGetEmployeeNotificationFormResponsesQuery}
                />
            },
            {
                label: 'Linked Self-service Forms',
                component: <NotificationFormsTable
                    data={linkedSelfServiceForms}
                    columns={linkedSelfServiceFormsTableColumns}
                    isLoading={isLoadingLinkedForms || isFetchingLinkedForms}
                    selectedRowIndex={selectedLinkedFormIndex}
                    setSelectedRowIndex={setSelectedLinkedFormIndex}
                    navigate={navigate}
                    personId={employeeIdNumber}
                    fetchResponsesFn={useLazyGetEmployeeNotificationFormResponsesQuery}
                />
            },
            {
                label: 'Approvals',
                component: <ApprovalFormsTable
                    employeeId={employeeIdNumber}
                    navigate={navigate}
                />
            },
            {
                label: 'Communications',
                component: <CommunicationFormsTable
                    variant="elevation"
                    id={`${key}/communication`}
                    columns={communicationFormsTableColumns}
                    tableProps={{
                        enableSorting: false,
                        enableTopToolbar: false,
                        enableFullScreenToggle: false
                    }}
                    onRowClick={(params) => navigate(`${params.requestId}`)}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No communication yet'
                    }}
                />
            }
        ];

    return (
        <Paper
            variant="outlined"
            sx={
                isSmallScreen
                    ? {}
                    : {
                        minHeight: '100%',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
        >
            <AdmicityTabs
                tabs={tabs}
                tabIndex={currentTabIndex}
                onChange={(index) => {
                    setCurrentTabIndex(index);
                    setSelectedNotificationFormIndex(undefined);
                }}
                displayDivider
                slots={{
                    panel: {
                        paddingRight: 0,
                        paddingLeft: 0
                    }
                }}
            />
        </Paper>
    );
};

EmployeeForms.propTypes = {
    employeeId: PropTypes.number
};

export default EmployeeForms;
